import { isElectronApp } from '#~/electron/utils.js';
import { isProduction as _isProduction, toPhase } from '@studio/models';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { isHydrated, toString } from '@tryiris-ai/js-types';
import deepFreeze from 'deep-freeze';
import type { Config, PostHogEnvironment } from './types.js';

const domainList: Record<string, string> = {
  '127.0.0.1': 'iris',
  'localhost': 'iris',
  'studio.lvh.me': 'iris',
  'studio.itg.tryiris.ai': 'iris',
  'studio.stg.tryiris.ai': 'iris',
  'studio.tryiris.ai': 'iris',
  'ptzoptics-studio.lvh.me': 'ptzoptics',
  'ptzoptics-studio.itg.tryiris.ai': 'ptzoptics',
  'ptzoptics-studio.tryiris.ai': 'ptzoptics',
  'ptzoptics-studio.stg.tryiris.ai': 'ptzoptics',
  'hive.ptzoptics.com': 'ptzoptics'
};

const defaultPosthogApiKey = 'phc_zlj958ZA84aBXI8O8AJuTur0MlIMfaBbUtNfozBn1lj';
// 'off' is the CI/e2e telemetry kill-switch: capture is disabled, while manual
// CI downloads still fetch flags. Only reachable through
// IRIS_POSTHOG_ENV in an Electron app — the web resolution path below never
// produces it, so shipped web/prod behavior is unchanged.
export type RuntimeVendor = 'iris' | 'ptzoptics';

const posthogApiHostMap: Record<RuntimeVendor, string> = {
  iris: 'https://e.tryiris.ai',
  ptzoptics: 'https://e.hive.tryiris.ai'
};

// Keyed by capture-enabled environments only — 'off' is resolved by
// resolvePosthogTelemetry BEFORE this map is consulted.
const posthogMap: Record<string, Record<string, string>> = {
  iris: {
    tyrus: 'phc_t4BwOKL07utEuzsk6EVNfs8RLj9JZYRwKuVfGypwIsl',
    // normal
    itg: 'phc_zlj958ZA84aBXI8O8AJuTur0MlIMfaBbUtNfozBn1lj',
    stg: 'phc_zlj958ZA84aBXI8O8AJuTur0MlIMfaBbUtNfozBn1lj',
    prod: 'phc_yJFf7uc1sK60nhkqNqm8XJDSdpvJk3kZboru71a20Ni'
  },
  ptzoptics: {
    tyrus: 'phc_zHTYAItJktGR008rVEiPhpBkKb3yNW3RBxt3GRrS6gs',
    itg: 'phc_zHTYAItJktGR008rVEiPhpBkKb3yNW3RBxt3GRrS6gs',
    stg: 'phc_zHTYAItJktGR008rVEiPhpBkKb3yNW3RBxt3GRrS6gs',
    prod: 'phc_dF9rcdXY2cOPgWKAHrxxEeaelxAlDm431s1aT8Bo4TI'
  }
};

/**
 * Vercel Git-integration hosts encode the project name (`<project>-git-<branch>-<team>.vercel.app`
 * and `<project>-<hash>-<team>.vercel.app`). Those names are unbounded, so they
 * cannot live in `domainList`. This table is the only product mapping: a host
 * whose parsed project is missing here stays unresolved (navy boot). Add a
 * row when a new Vercel project should boot Studio; do not special-case
 * hostname strings at the call site.
 */
export const VERCEL_PROJECT_VENDOR = {
  studio: 'iris',
  hive: 'ptzoptics'
} as const satisfies Record<string, RuntimeVendor>;

export type VercelProjectName = keyof typeof VERCEL_PROJECT_VENDOR;

/** Team slug in default Vercel Git hosts (`*-irisai.vercel.app`). */
export const VERCEL_TEAM_SLUG = 'irisai';

const VERCEL_APP_SUFFIX = '.vercel.app';
const VERCEL_GIT_MARKER = '-git-';

/**
 * Parse the Vercel project name out of a default Git-integration host.
 * Custom aliases (`studio-client-phi.vercel.app`) have no project segment and
 * return undefined — those belong in `domainList` if they should boot.
 */
export const vercelProjectFromHostname = (hostname: string): string | undefined => {
  if (!hostname.endsWith(VERCEL_APP_SUFFIX)) {
    return undefined;
  }

  const label = hostname.slice(0, -VERCEL_APP_SUFFIX.length);
  if (label.length === 0 || label.includes('.')) {
    return undefined;
  }

  const teamSuffix = `-${VERCEL_TEAM_SLUG}`;
  if (!label.endsWith(teamSuffix)) {
    return undefined;
  }

  const withoutTeam = label.slice(0, -teamSuffix.length);
  const gitAt = withoutTeam.indexOf(VERCEL_GIT_MARKER);
  if (gitAt > 0) {
    return withoutTeam.slice(0, gitAt);
  }

  const hashAt = withoutTeam.lastIndexOf('-');
  if (hashAt <= 0) {
    return withoutTeam;
  }

  return withoutTeam.slice(0, hashAt);
};

const isVercelProjectName = (project: string): project is VercelProjectName =>
  Object.prototype.hasOwnProperty.call(VERCEL_PROJECT_VENDOR, project);

const vendorFromVercelPreviewHost = (hostname: string): RuntimeVendor | undefined => {
  const project = vercelProjectFromHostname(hostname);
  if (project === undefined || !isVercelProjectName(project)) {
    return undefined;
  }

  return VERCEL_PROJECT_VENDOR[project];
};

/**
 * Resolve only an explicit supported vendor or an approved hosted domain.
 * Unknown identity is intentionally not mapped to Iris: authentication,
 * analytics, update, and billing routes all derive from this decision.
 */
export const resolveRuntimeVendor = (
  explicitVendor: string | undefined,
  hostname: string
): RuntimeVendor | undefined => {
  if (explicitVendor === 'iris' || explicitVendor === 'ptzoptics') {
    return explicitVendor;
  }

  if (explicitVendor) {
    return undefined;
  }

  const domainVendor = domainList[hostname] ?? vendorFromVercelPreviewHost(hostname);
  return domainVendor === 'iris' || domainVendor === 'ptzoptics' ? domainVendor : undefined;
};

/**
 * Resolve the PostHog API key + capture switch for a posthog environment.
 *
 * 'off' (the CI/e2e kill-switch, ENG: disable PostHog reporting in CI) means
 * "capture disabled": manual downloads fetch staging flags and automated E2E
 * bootstraps explicit local flags. Both need a real key, pinned to itg.
 *
 * Any OTHER unresolved environment intentionally resolves NO key: per the
 * product-identity contract (resolveRuntimeVendor above), unknown identity
 * or environment must not silently inherit Iris credentials, and boot
 * proceeds without PostHog ("preserve boot when PostHog is unavailable" —
 * root.tsx skips PostHog init when the key is undefined).
 *
 * Exported as a pure function so the kill-switch resolution is unit-testable
 * without a window/Electron harness.
 * Per rule: ai-code-requires-tests + reference-rules-in-comments
 * (ai.knowledge/global) — see config/utils.test.ts.
 */
export const resolvePosthogTelemetry = (
  posthogEnv: PostHogEnvironment,
  vendorKey: string
): { posthogApiKey: string | undefined; posthogCaptureDisabled: boolean } => {
  const posthogCaptureDisabled = posthogEnv === 'off';
  const keyEnv = posthogCaptureDisabled ? 'itg' : posthogEnv;
  const mappedKey = posthogMap[vendorKey]?.[keyEnv];

  return {
    // The 'off' path needs a real key for live or bootstrapped flags; every
    // other unresolved path yields undefined so PostHog is skipped entirely.
    posthogApiKey: posthogCaptureDisabled ? (mappedKey ?? defaultPosthogApiKey) : mappedKey,
    posthogCaptureDisabled
  };
};

/**
 * Converts the Vite environment into a normalized runtime configuration.
 *
 * @param env - Vite environment (https://vitejs.dev/guide/env-and-mode.html).
 * @returns Normalizedß runtime configuration.
 */
export const toConfig = (env: T_Optional<ImportMetaEnv>): T_Maybe<Config> => {
  const phase = toPhase(env.MODE);

  const cdn = {
    endpoint: toString(env.IRIS_CDN_ENDPOINT)
  };

  const liveKit = {
    endpoint:
      localStorage.getItem('__override.livekit_endpoint') ?? toString(env.IRIS_LIVEKIT_ENDPOINT)
  };

  const nats = {
    endpoint: toString(env.IRIS_NATS_ENDPOINT)
  };

  const vendor = toString(env.IRIS_VENDOR);
  const key = resolveRuntimeVendor(vendor, window.location.hostname);

  // Product identity is an authoritative boundary. A missing or unknown value
  // must stop configuration rather than inherit Iris credentials and routes.
  if (!key) {
    return undefined;
  }

  const oauth = {
    iris: {
      audience: toString(env.IRIS_OAUTH_AUDIENCE),
      clientIdWeb: toString(env.IRIS_OAUTH_CLIENT_WEB),
      clientIdDesktop: toString(env.IRIS_OAUTH_CLIENT_DESKTOP),
      url: toString(env.IRIS_OAUTH_URL)
    },

    ptzoptics: {
      audience: toString(env.IRIS_VENDOR_PTZOPTICS_OAUTH_AUDIENCE),
      clientIdWeb: toString(env.IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_WEB),
      clientIdDesktop: toString(env.IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_DESKTOP),
      url: toString(env.IRIS_VENDOR_PTZOPTICS_OAUTH_URL)
    }
  }[key];

  const sentry = {
    dsn: toString(env.IRIS_SENTRY_DSN)
  };

  const version = toString(env.IRIS_VERSION);

  if (
    !isHydrated(cdn) ||
    !isHydrated(liveKit) ||
    !isHydrated(nats) ||
    !isHydrated(oauth) ||
    !isHydrated(sentry) ||
    !phase ||
    !version
  ) {
    return undefined;
  }

  const isProduction = _isProduction(phase);

  const isLocalDev = (): boolean => {
    if (isElectronApp()) {
      return false;
    }

    const hostname = window.location.hostname;

    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.lvh.me');
  };

  const extractEnvFromURL = (): Exclude<PostHogEnvironment, 'tyrus'> => {
    if (isElectronApp()) {
      return env.IRIS_ENV ?? 'unknown';
    }

    const segments = window.location.hostname.split('.');

    if (segments.length === 4) {
      const [_, subdomain] = segments;

      if (subdomain === 'itg') {
        return 'itg';
      }

      if (subdomain === 'stg') {
        return 'stg';
      }
    }

    if (segments.length === 3) {
      const [_, vendor] = segments;

      if (vendor === 'ptzoptics' || vendor === 'tryiris' || vendor === 'iris') {
        return 'prod';
      }
    }

    return 'unknown';
  };

  const currEnv = extractEnvFromURL();
  const resolvePosthogEnv = (): PostHogEnvironment => {
    if (isElectronApp()) {
      const posthogEnv = env.IRIS_POSTHOG_ENV ?? env.IRIS_ENV;

      // 'off' = CI/e2e kill-switch, forwarded from the Electron main process
      // via get-env-config (src/main/ipc.ts), including downloadable CI builds.
      if (
        posthogEnv === 'tyrus' ||
        posthogEnv === 'itg' ||
        posthogEnv === 'stg' ||
        posthogEnv === 'prod' ||
        posthogEnv === 'off'
      ) {
        return posthogEnv;
      }

      return 'unknown';
    }

    return isLocalDev() ? 'tyrus' : currEnv;
  };

  const posthogEnv = resolvePosthogEnv();
  // `key` is fail-closed above (resolveRuntimeVendor guard), so the vendor is
  // always a mapped product here — no unknown-vendor fallback is needed.
  const { posthogApiKey, posthogCaptureDisabled } = resolvePosthogTelemetry(posthogEnv, key);
  const posthogApiHost = posthogApiHostMap[key];

  // @ts-expect-error: Deep freeze the configuration.
  return deepFreeze({
    cdn,
    isProduction,
    liveKit,
    nats,
    oauth,
    phase,
    sentry,
    version,
    frontendUrl:
      sanitizeURL(env.IRIS_FRONTEND_URL) ?? window.location.protocol + '//' + window.location.host,
    vendor: key,
    electronVersion: env.IRIS_ELECTRON_VERSION,
    appVersion: toString(env.IRIS_APP_VERSION),
    apiEndpoint: toString(env.IRIS_API_ENDPOINT),
    env: currEnv,
    posthogApiKey,
    posthogApiHost,
    posthogEnv,
    posthogCaptureDisabled,
    buildInfoAllowedEmails: toString(env.IRIS_BUILD_INFO_ALLOWED_EMAILS),
    noMediaInputSandbox: env.IRIS_NO_MEDIA_INPUT_SANDBOX === '1'
  });
};

const sanitizeURL = (url: string | undefined) => {
  if (!url) {
    return undefined;
  }

  // if url ends with a slash, remove it
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  }

  return url;
};
